import { useMemo, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatMoney, toCents, type Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink, PeekButton } from '../components/RecordLink.js';
import { ListToolbar, ListWindowNotice, type FilterChip } from '../components/ListToolbar.js';
import { SortHeader } from '../components/SortHeader.js';
import { Pager } from '../components/Pager.js';
import { downloadExport, useListView } from '../lib/listView.js';

/**
 * Invoices.
 *
 * Two things about the ordering, both of which were wrong and both of which mattered:
 *
 * **Newest first.** It sorted by due date ascending whatever the status filter said, so choosing
 * "Payée" opened on the oldest settled invoice in the archive — the least interesting row in the
 * database. Urgency is still one click away: the "en retard" filter, or the échéance heading.
 *
 * **The period.** There was none, so the list reached back through every invoice ever issued.
 * It now opens on the account's default window and says, in the space where the missing rows
 * would be, exactly how many it is holding back.
 */

type InvoiceRow = {
  id: string;
  number: string | null;
  status: string;
  total: string;
  paidTotal: string;
  balance: string;
  issuedOn: string | null;
  dueOn: string | null;
  firstViewedAt: string | null;
  /** Nested, the way the API actually sends it. This was declared flat, so every row read `—`. */
  customer: { id: string | null; name: string | null };
  job: { id: string; number: string } | null;
};

type InvoicesResponse = {
  items: InvoiceRow[];
  total: number;
  page?: number;
  pageSize?: number;
  pageCount?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  /** Rows the period is holding back, counted by the API rather than guessed at here. */
  outsideWindow: number;
  receivable: string;
  overdue: string;
  unbilled: string;
};

const STATUSES = ['draft', 'awaiting_payment', 'partial', 'paid', 'voided'] as const;

export function InvoicesScreen() {
  const { t: translate, i18n } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';

  const search = useSearch({ from: '/invoices' });
  const navigate = useNavigate();
  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  const view = useListView({
    routeId: '/invoices',
    defaultSort: 'issued',
    timeBased: true,
    defaultDateField: 'issued',
    extra: useMemo(
      () => ({ status: search.status, overdueOnly: search.overdueOnly }),
      [search.status, search.overdueOnly],
    ),
  });

  // Filtering happens on the server, so the count shown is the count of everything that
  // matches rather than of whatever the first page contained.
  const invoices = useQuery({
    queryKey: ['invoices', view.params.toString()],
    queryFn: () => request<InvoicesResponse>(`/invoices?${view.params}`),
    placeholderData: (prev) => prev,
  });

  const data = invoices.data;
  const rows = data?.items ?? [];
  const now = Date.now();
  const receivable = toCents(data?.receivable ?? '0');
  // Kept apart from receivables on purpose: a draft was never sent, so it is unbilled work
  // rather than money owed.
  const unbilled = toCents(data?.unbilled ?? '0');

  const chips: FilterChip[] = [
    ...(search.status !== 'all'
      ? [
          {
            id: 'status',
            label: `${t('common.status')} : ${t(`invoice.status.${search.status}`)}`,
            onRemove: () => navigate({ to: '.', search: (p: any) => ({ ...p, status: 'all', page: undefined }), replace: true }),
          },
        ]
      : []),
    ...(search.overdueOnly
      ? [
          {
            id: 'overdue',
            label: t('invoice.overdueOnly'),
            onRemove: () =>
              navigate({ to: '.', search: (p: any) => ({ ...p, overdueOnly: false, page: undefined }), replace: true }),
          },
        ]
      : []),
  ];

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const outcome = await downloadExport('/invoices/export', view.exportParams, locale);
      setExportNote({
        text: outcome.truncated
          ? t('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : t('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: t('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="screen">
      <header className="screen__head">
        <h1 className="screen__title">{t('invoice.many')}</h1>
        <div className="invoices__figures">
          <span className="money money--due invoices__figure">{t('invoice.receivable', { amount: formatMoney(receivable, locale) })}</span>
          {unbilled > 0 && (
            <span className="invoices__unbilled" title={t('invoice.unbilledHint')}>
              {t('invoice.unbilledAmount', { amount: formatMoney(unbilled, locale) })}
            </span>
          )}
        </div>
      </header>

      <ListToolbar
        view={view}
        searchPlaceholder={t('invoice.searchPlaceholder')}
        chips={chips}
        onClearAll={() =>
          navigate({ to: '.', search: (p: any) => ({ ...p, status: 'all', overdueOnly: false, page: undefined }), replace: true })
        }
        dateFields={[
          { value: 'issued', label: t('list.field.issued') },
          { value: 'due', label: t('list.field.due') },
          { value: 'paid', label: t('list.field.paid') },
          { value: 'created', label: t('list.field.created') },
        ]}
        total={data?.total}
        onExport={() => void runExport()}
        exporting={exporting}
        exportNote={exportNote?.text ?? null}
        exportFailed={exportNote?.failed}
      >
        <label className="listbar__field">
          {t('common.status')}
          <select
            value={search.status}
            onChange={(e) =>
              navigate({ to: '.', search: (p: any) => ({ ...p, status: e.target.value, page: undefined, cursor: undefined }), replace: true })
            }
          >
            <option value="all">{t('common.all')}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`invoice.status.${value}`)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="listbar__toggle"
          aria-pressed={search.overdueOnly}
          onClick={() =>
            navigate({
              to: '.',
              search: (p: any) => ({ ...p, overdueOnly: !search.overdueOnly, page: undefined, cursor: undefined }),
              replace: true,
            })
          }
        >
          {t('invoice.overdueOnly')}
        </button>
      </ListToolbar>

      <ListWindowNotice
        view={view}
        outsideWindow={data?.outsideWindow ?? 0}
        empty={rows.length === 0 && !invoices.isPending}
      />

      {invoices.isError && (
        <div className="notice notice--error" role="alert">
          <p>{t('errors.generic')}</p>
          <button type="button" onClick={() => void invoices.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {(invoices.isPending || rows.length > 0) && (
        <table className="table table--dense" aria-label={t('invoice.many')}>
          <thead>
            <tr>
              <SortHeader field="number" label={t('list.column.number')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="customer" label={t('list.column.customer')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              {/* Not sortable, and honestly so: alphabetical order over five status words
                  answers no question anybody has. The filter above does. */}
              <th scope="col">
                <span className="label">{t('common.status')}</span>
              </th>
              <SortHeader field="issued" label={t('list.column.issuedOn')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="due" label={t('list.column.dueOn')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} />
              <SortHeader field="total" label={t('list.column.total')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} numeric />
              <SortHeader field="balance" label={t('list.column.balance')} sort={data?.sort} dir={data?.dir} onSort={view.setSort} numeric />
              <th scope="col" aria-label={t('list.column.actions')} />
            </tr>
          </thead>
          <tbody>
            {invoices.isPending && (
              <tr>
                <td colSpan={8} className="cell cell--loading">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {rows.map((invoice) => {
              const overdue = toCents(invoice.balance) > 0 && invoice.dueOn && new Date(invoice.dueOn).getTime() < now;
              return (
                <tr key={invoice.id}>
                  <td className="cell">
                    <RecordLink kind="invoice" id={invoice.id} className="cell__link">
                      <span className="tnum">{invoice.number || t('invoice.noNumber')}</span>
                    </RecordLink>
                    {/* Only worth saying while money is still owed: it tells you whether
                        chasing is warranted yet. On a settled invoice it is just noise. */}
                    {!invoice.firstViewedAt && toCents(invoice.balance) > 0 && (
                      <span className="cell__sub">{t('invoice.neverOpened')}</span>
                    )}
                  </td>
                  <td className="cell cell--name">
                    {invoice.customer?.id ? (
                      <RecordLink kind="customer" id={invoice.customer.id} className="cell__link">
                        {invoice.customer.name ?? '—'}
                      </RecordLink>
                    ) : (
                      <span className="cell__none">—</span>
                    )}
                  </td>
                  <td className="cell">{t(`invoice.status.${invoice.status}`)}</td>
                  <td className="cell cell--activity">{day(invoice.issuedOn, locale)}</td>
                  <td className={`cell cell--activity${overdue ? ' cell__sub--overdue' : ''}`}>{day(invoice.dueOn, locale)}</td>
                  <td className="cell numeric">
                    <span className="money">{formatMoney(toCents(invoice.total), locale)}</span>
                  </td>
                  <td className="cell numeric">
                    <span className={`money${overdue ? ' money--overdue' : ' money--due'}`}>
                      {formatMoney(toCents(invoice.balance), locale)}
                    </span>
                  </td>
                  <td className="cell" style={{ width: 32 }}>
                    <PeekButton kind="invoice" id={invoice.id} label={t('invoice.preview')} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {!invoices.isPending && rows.length === 0 && (
        <div className="empty">
          <h2 className="empty__title">{t('common.noResults')}</h2>
          <p className="empty__body">{t('invoice.emptyFiltered')}</p>
        </div>
      )}

      {data && (
        <Pager
          page={data.page ?? view.page}
          pageCount={data.pageCount ?? 1}
          pageSize={data.pageSize ?? view.pageSize}
          total={data.total}
          count={rows.length}
          label={t('invoice.many').toLowerCase()}
          busy={invoices.isFetching}
          onPage={view.setPage}
          onPageSize={view.setPageSize}
        />
      )}
    </div>
  );
}

/** A date, in the reader's convention. Null reads as an em dash, never as an empty cell. */
function day(iso: string | null, locale: Locale): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA');
}
